import { useCallback, useRef, useState } from 'react';

import { useLaunchBatchMutation } from './api';

export interface BulkLaunchController {
  /** True while the confirm step is open. */
  isConfirming: boolean;
  isLaunching: boolean;
  /** Set when a launch attempt failed; the same attempt can be retried. */
  errorMessage?: string;
  /** The batch this launch produced, once it exists. */
  launchedBatchId?: string;
  openConfirm: () => void;
  closeConfirm: () => void;
  confirm: () => void;
  /**
   * Forgets the launched batch, so the next open reads the latest batch from
   * the list rather than pinning this one while a newer batch runs.
   */
  forgetLaunch: () => void;
}

/**
 * The bulk launch itself (AC-12), and AC-13's guarantee on the client side.
 *
 * **The idempotency key is minted when the confirm step OPENS, not when the
 * button is clicked.** The contract asks for exactly this, and it is the whole
 * of the guarantee here: a key minted per click makes a double-click two
 * genuinely different requests, and no server-side de-duplication can undo
 * that, because nothing links them. Minted once per confirm step, an impatient
 * second click, a double-click and a retry after a timeout all carry the same
 * key and collapse into one batch.
 *
 * The key therefore outlives a failed attempt on purpose. It is discarded only
 * when the confirm step closes or a launch succeeds — a retry that minted a
 * fresh one would be a second batch wearing the word "retry".
 */
export const useBulkLaunch = (
  organizationId: string | null | undefined,
  definitionId: string,
): BulkLaunchController => {
  const [launchBatch, { isLoading: isLaunching }] = useLaunchBatchMutation();
  const [isConfirming, setIsConfirming] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [launchedBatchId, setLaunchedBatchId] = useState<string | undefined>();

  /** One key per confirm step. See the note above — this is not per click. */
  const idempotencyKeyRef = useRef<string | null>(null);

  const openConfirm = useCallback(() => {
    idempotencyKeyRef.current = crypto.randomUUID();
    setErrorMessage(undefined);
    setIsConfirming(true);
  }, []);

  const closeConfirm = useCallback(() => {
    idempotencyKeyRef.current = null;
    setIsConfirming(false);
    setErrorMessage(undefined);
  }, []);

  const confirm = useCallback(() => {
    const idempotencyKey = idempotencyKeyRef.current;
    if (!organizationId || !idempotencyKey) return;

    setErrorMessage(undefined);
    launchBatch({
      orgId: organizationId,
      definitionId,
      batchLaunchRequest: { idempotency_key: idempotencyKey },
    })
      .unwrap()
      .then(batch => {
        idempotencyKeyRef.current = null;
        setIsConfirming(false);
        setLaunchedBatchId(batch.batch_id);
      })
      .catch((error: unknown) => {
        // The key is deliberately NOT cleared here: the next attempt is a
        // retry of this launch, not a new one, and it must carry the same key
        // or a request that actually landed before timing out would be sent
        // twice.
        const detail = (error as { data?: { message?: string } })?.data?.message;
        setErrorMessage(detail ?? 'The send could not be started. Please try again.');
      });
  }, [launchBatch, organizationId, definitionId]);

  const forgetLaunch = useCallback(() => setLaunchedBatchId(undefined), []);

  return {
    isConfirming,
    isLaunching,
    errorMessage,
    launchedBatchId,
    openConfirm,
    closeConfirm,
    confirm,
    forgetLaunch,
  };
};
